/// <amd-module name="Wit/Common/TeamSettingsPanel/Services/TeamSettingsPanelService" />
import { ITeamSettings } from "Agile/Contracts/Common";
import { ITfsPageService } from "Tfs/Platform/Page";
import {
    IObservable,
    IObservableValue,
    IReadonlyObservableValue,
    ObservableValue
} from "VSS/Core/Observable";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { BacklogConfiguration } from "Wit/Clients/Work/Contracts/Work";
import { ITeamSettingsPatch, ITeamSettingsService } from "Wit/Common/TeamServices/TeamSettingsService";
import * as Resources from "Wit/Common/TeamSettingsPanel/Resources";
import { formatColor } from "Wit/Common/Utilities/FormatColor";
import {
    IBacklogVisibilitySetting,
    ITeamPanelSettings,
    ITeamPermissions,
    IWorkingDay
} from "../Contracts/Contracts";

enum DataProviders {
    backlogConfiguration = "ms.vss-work-web.agile-backlog-configuration-data-provider",
    teamPermissions = "ms.vss-tfs.team-permissions-data-provider"
}

enum TeamPanelSettingKeys {
    backlogVisibilitySettings = "backlogVisibilitySettings",
    workingDays = "workingDays",
    bugsBehavior = "bugsBehavior"
}

enum DayOfWeek {
    Sunday = 0,
    Monday = 1,
    Tuesday = 2,
    Wednesday = 3,
    Thursday = 4,
    Friday = 5,
    Saturday = 6
}

export interface ITeamSettingsPanelService extends IVssService {
    getIsBusy(): IReadonlyObservableValue<boolean>;
    getIsDirty(): IReadonlyObservableValue<boolean>;
    getIsValid(): IReadonlyObservableValue<boolean>;
    getTeamPanelSettings(options?: { ignoreCache?: boolean }): IReadonlyObservableValue<ITeamPanelSettings | undefined>;
    getTeamPermissions(teamId: string): IReadonlyObservableValue<ITeamPermissions | undefined>;
    setIsValid(enabled: boolean): void;
    updateIsDirty(): void;
    updateTeamSettings(teamId: string): Promise<boolean>;
}

class TeamSettingsPanelService extends VssService implements ITeamSettingsPanelService {
    private backlogConfiguration: BacklogConfiguration | undefined;
    private registeredCallbacks: { observable: IObservable<any>; callback: (value: any, action?: string | undefined) => void }[] = [];
    private readonly isBusy = new ObservableValue<boolean>(false);
    private readonly isDirty = new ObservableValue<boolean>(false);
    private isDirtyIndividual: { [setting: string]: boolean } = {};
    private readonly isValid = new ObservableValue<boolean>(true);
    private readonly permissionsCache: { [teamId: string]: IObservableValue<ITeamPermissions | undefined> } = {};
    private teamPanelSettings: IObservableValue<ITeamPanelSettings | undefined>;
    private teamPanelSettingsUnchanged: ITeamPanelSettings | undefined; // used to check if settings are dirty
    private teamSettings: IReadonlyObservableValue<ITeamSettings | undefined>;

    public _serviceEnd() {
        for (const { observable, callback } of this.registeredCallbacks) {
            observable.unsubscribe(callback);
        }
    }

    public getIsBusy = (): IReadonlyObservableValue<boolean> => {
        return this.isBusy;
    };

    public getIsDirty = (): IReadonlyObservableValue<boolean> => {
        return this.isDirty;
    };

    public getIsValid = (): IReadonlyObservableValue<boolean> => {
        return this.isValid;
    };

    public getTeamPanelSettings = (options?: { ignoreCache?: boolean }): IReadonlyObservableValue<ITeamPanelSettings | undefined> => {
        if (!this.teamPanelSettings || options?.ignoreCache) {
            this.teamPanelSettings = new ObservableValue<ITeamPanelSettings | undefined>(undefined);
            this.initialize(options?.ignoreCache);

            const teamSettings = this.teamSettings.value;
            if (teamSettings) {
                this.loadTeamPanelSettings(teamSettings);
            } else {
                const callback = this.teamSettings.subscribe(this.loadTeamPanelSettings);
                this.registeredCallbacks.push({ observable: this.teamSettings, callback });
            }
        }

        return this.teamPanelSettings;
    };

    public getTeamPermissions = (teamId: string): IReadonlyObservableValue<ITeamPermissions | undefined> => {
        if (!this.permissionsCache[teamId]) {
            this.permissionsCache[teamId] = new ObservableValue<ITeamPermissions | undefined>(undefined);
            const tfsPageData = this.pageContext.getService<ITfsPageService>("ITfsPageService").getData();
            const projectId = tfsPageData?.project?.id ?? "";

            const context = {
                projectId,
                teamId
            };

            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            this.isBusy.value = true;
            contributionService.getDataAsync<{ permissions: ITeamPermissions }>(DataProviders.teamPermissions, context).then(result => {
                this.permissionsCache[teamId].value = result?.permissions ?? {
                    CurrentUserHasTeamAdminPermission: false,
                    CurrentUserHasTeamPermission: false
                };
                this.isBusy.value = false;
            });
        }

        return this.permissionsCache[teamId];
    };

    public setIsValid = (isValid: boolean) => {
        this.isValid.value = isValid;
    };

    public updateIsDirty = () => {
        let isDirty = false;
        let bugsBehaviorAndWorkingDaysEnabled = true;
        const backlogVisibilitySettings = this.teamPanelSettings.value?.backlogVisibilitySettings;
        if (backlogVisibilitySettings && !backlogVisibilitySettings.find(item => item.isRequirementmentBacklog && item.checked.value)) {
            bugsBehaviorAndWorkingDaysEnabled = false;
        }
        if (this.teamPanelSettingsUnchanged) {
            if (
                this.teamPanelSettingsUnchanged.backlogVisibilitySettings.find(
                    (setting, index) =>
                        setting.id === this.teamPanelSettings.value?.backlogVisibilitySettings[index]?.id &&
                        setting.checked.value !== this.teamPanelSettings.value?.backlogVisibilitySettings[index]?.checked.value
                )
            ) {
                this.isDirtyIndividual[TeamPanelSettingKeys.backlogVisibilitySettings] = true;
                isDirty = true;
            } else {
                this.isDirtyIndividual[TeamPanelSettingKeys.backlogVisibilitySettings] = false;
            }

            if (
                bugsBehaviorAndWorkingDaysEnabled &&
                this.teamPanelSettingsUnchanged.workingDays.find(
                    (setting, index) =>
                        setting.id === this.teamPanelSettings.value?.workingDays[index]?.id &&
                        setting.isWorkday.value !== this.teamPanelSettings.value?.workingDays[index]?.isWorkday.value
                )
            ) {
                this.isDirtyIndividual[TeamPanelSettingKeys.workingDays] = true;
                isDirty = true;
            } else {
                this.isDirtyIndividual[TeamPanelSettingKeys.workingDays] = false;
            }

            if (
                bugsBehaviorAndWorkingDaysEnabled &&
                this.teamPanelSettingsUnchanged.bugsBehavior.value !== this.teamPanelSettings.value?.bugsBehavior.value
            ) {
                this.isDirtyIndividual[TeamPanelSettingKeys.bugsBehavior] = true;
                isDirty = true;
            } else {
                this.isDirtyIndividual[TeamPanelSettingKeys.bugsBehavior] = false;
            }
        }

        this.isDirty.value = isDirty;
    };

    public updateTeamSettings = (teamId: string): Promise<boolean> => {
        const teamSettingsService = this.pageContext.getService<ITeamSettingsService>("ITeamSettingsService");
        const patch = getTeamSettingsPatch(this.teamPanelSettings.value!, this.isDirtyIndividual);

        return teamSettingsService
            .updateTeamSettings(patch, teamId)
            .then((result: boolean | void) => {
                if (result === false) {
                    return false;
                }

                return true;
            });
    };

    private getBacklogConfiguration = () => {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        this.backlogConfiguration = contributionService.getData<BacklogConfiguration>(DataProviders.backlogConfiguration);
    };

    private getTeamSettings = () => {
        const teamSettingsService = this.pageContext.getService<ITeamSettingsService>("ITeamSettingsService");
        this.teamSettings = teamSettingsService.getTeamSettingsObservable();
    };

    private initialize = (ignoreCache?: boolean) => {
        this.isDirty.value = false;
        this.isValid.value = true;
        this.isDirtyIndividual = {};

        if (!this.teamSettings || ignoreCache) {
            this.getTeamSettings();
        }

        if (!this.backlogConfiguration || ignoreCache) {
            this.getBacklogConfiguration();
        }
    };

    private loadTeamPanelSettings = (teamSettings?: ITeamSettings) => {
        if (
            teamSettings &&
            this.backlogConfiguration &&
            this.backlogConfiguration.portfolioBacklogs &&
            this.backlogConfiguration.requirementBacklog
        ) {
            // account for different WebAccess/Client contracts
            let isBugsBehaviorConfigured: boolean;
            if ("isBugsBehaviorConfigured" in this.backlogConfiguration) {
                isBugsBehaviorConfigured = this.backlogConfiguration.isBugsBehaviorConfigured;
            } else {
                const hasProp = "isBugsBehaviorConfigValid" in this.backlogConfiguration;
                isBugsBehaviorConfigured = hasProp && this.backlogConfiguration["isBugsBehaviorConfigValid"];
            }

            const teamPanelSettings: ITeamPanelSettings = {
                backlogVisibilitySettings: getBacklogVisibilitySettings(teamSettings, this.backlogConfiguration),
                workingDays: getWorkingDays(teamSettings),
                bugsBehavior: new ObservableValue<string>(
                    teamSettings.bugsBehavior?.toString() ?? this.backlogConfiguration.bugsBehavior?.toString()
                ),
                isBugsBehaviorValid: isBugsBehaviorConfigured
            };

            // create duplicate used to check if panel is dirty
            this.teamPanelSettingsUnchanged = {
                backlogVisibilitySettings: getBacklogVisibilitySettings(teamSettings, this.backlogConfiguration),
                workingDays: getWorkingDays(teamSettings),
                bugsBehavior: new ObservableValue<string>(
                    teamSettings.bugsBehavior?.toString() ?? this.backlogConfiguration.bugsBehavior?.toString()
                ),
                isBugsBehaviorValid: isBugsBehaviorConfigured
            };

            this.teamPanelSettings.value = teamPanelSettings;
        }
    };
}

Services.add("ITeamSettingsPanelService", { serviceFactory: TeamSettingsPanelService });

export function getTeamSettingsPanelService(pageContext: IVssPageContext): ITeamSettingsPanelService {
    return pageContext.getService<ITeamSettingsPanelService>("ITeamSettingsPanelService");
}

function getBacklogVisibilitySettings(settings: ITeamSettings, configuration: BacklogConfiguration): IBacklogVisibilitySetting[] {
    const backlogVisibilities = settings.backlogVisibilities;
    const configs = [...configuration.portfolioBacklogs, configuration.requirementBacklog].sort((a, b) => b.rank - a.rank);

    const visibleBacklogLevels: IBacklogVisibilitySetting[] = configs.map(config => ({
        color: formatColor(config.color),
        id: config.id,
        name: config.name,
        checked: new ObservableValue<boolean>(backlogVisibilities[config.id]),
        isRequirementmentBacklog: config.id === configuration.requirementBacklog.id
    }));

    return visibleBacklogLevels;
}

function getTeamSettingsPatch(teamSettings: ITeamPanelSettings, dirtyState: { [setting: string]: boolean }): ITeamSettingsPatch {
    const patch: ITeamSettingsPatch = {};

    if (dirtyState[TeamPanelSettingKeys.backlogVisibilitySettings]) {
        patch.backlogVisibilities = {};
        teamSettings.backlogVisibilitySettings.forEach(item => (patch.backlogVisibilities![item.id] = item.checked.value));
    }

    if (dirtyState[TeamPanelSettingKeys.workingDays]) {
        patch.workingDays = teamSettings.workingDays.filter(day => day.isWorkday.value).map(day => day.id);
    }

    if (dirtyState[TeamPanelSettingKeys.bugsBehavior]) {
        patch.bugsBehavior = Number(teamSettings.bugsBehavior.value);
    }

    return patch;
}

function getWorkingDays(settings: ITeamSettings): IWorkingDay[] {
    const workingDays: IWorkingDay[] = [
        { name: Resources.DayMonday, isWorkday: new ObservableValue<boolean>(true), id: DayOfWeek.Monday },
        { name: Resources.DayTuesday, isWorkday: new ObservableValue<boolean>(true), id: DayOfWeek.Tuesday },
        { name: Resources.DayWednesday, isWorkday: new ObservableValue<boolean>(true), id: DayOfWeek.Wednesday },
        { name: Resources.DayThursday, isWorkday: new ObservableValue<boolean>(true), id: DayOfWeek.Thursday },
        { name: Resources.DayFriday, isWorkday: new ObservableValue<boolean>(true), id: DayOfWeek.Friday },
        { name: Resources.DaySaturday, isWorkday: new ObservableValue<boolean>(true), id: DayOfWeek.Saturday },
        { name: Resources.DaySunday, isWorkday: new ObservableValue<boolean>(true), id: DayOfWeek.Sunday }
    ];

    const currentWeekends: { [id: number]: boolean } = {};
    settings.weekends.days.forEach(day => (currentWeekends[day] = true));

    workingDays.forEach(day => (day.isWorkday.value = !currentWeekends[day.id]));

    return workingDays;
}
